import { useState } from 'react'
import { Check, DoorClosed, UserMinus, UserPlus, X } from 'lucide-react'
import { ApiError, Button, EndpointHint, LoadFailure, Skeleton, isNotFoundError, useLoadingWindow } from '../../core/index.js'
import { schedulingRulesOf, useFacilityConfig } from '../../facilities/index.js'
import { useBoardActions, useScheduleBoard, useScheduleCoverage } from '../hooks.js'
import type { Posting, ScheduleBoardView, ScheduleCoverageView, SwapRequest } from '../types.js'
import { CoverageGaps } from './CoverageGaps.js'
import { describePeriod } from './preferencesText.js'
import { CHANGE_STATUS_LABEL, PHASE_CLASS, PHASE_LABEL, SOURCE_LABEL, SWAP_STATUS_CLASS, SWAP_STATUS_LABEL, describeApprovalReasons, describeLedgerEntry, describeOverridden, describePostingShift, describeShiftById, formatWhen, unitNamesOf, type UnitNames } from './boardText.js'

/** How many seats the board lists before Show all. */
const POSTINGS_SHOWN = 20

const INPUT = 'rounded border border-marine/30 bg-white px-2 py-1 text-[11px] text-ink focus:outline-none focus:border-marine'

function isNotReleased(error: unknown): boolean {
  return error instanceof ApiError && error.status === 409
}

/** The postings, newest shift first, with what the clock has done to each. */
function PostingsTable({ board, names, units, onAssign }: { board: ScheduleBoardView; names: ReadonlyMap<string, string>; units: UnitNames; onAssign?: (posting: Posting) => void }) {
  const open = board.postings.filter((posting) => posting.status === 'open')
  const filled = board.postings.filter((posting) => posting.status !== 'open')
  const [all, setAll] = useState(false)
  if (board.postings.length === 0) {
    return <p className="text-[11px] text-ink-mid">Nothing on the board: every published shift is held.</p>
  }
  // A month left short has hundreds of seats on the board: the first few, then the rest on request.
  const listed = [...open, ...filled]
  const shown = all ? listed : listed.slice(0, POSTINGS_SHOWN)
  return (
    <div className="flex flex-col gap-1">
      <ul className="divide-y divide-marine/10 text-[11px]">
        {shown.map((posting) => {
          const live = posting.offers.find((offer) => offer.answer === null)
          return (
            <li key={posting.postingId} className="flex flex-wrap items-center justify-between gap-2 py-1.5">
              <div className="min-w-0">
                <span className="font-bold text-ink">{describePostingShift(posting.shift, units)}</span>
                <span className="text-ink-mid"> · {SOURCE_LABEL[posting.source]}</span>
                {posting.vacatedFrom && <span className="text-ink-mid"> ({names.get(posting.vacatedFrom.workerId) ?? posting.vacatedFrom.workerId}: {posting.vacatedFrom.reason})</span>}
                <div className="text-[10px] text-ink-mid">
                  {posting.status === 'filled' ? (
                    <>
                      Taken by {names.get(posting.filledBy ?? '') ?? posting.filledBy} {posting.pickupMode === 'override' ? `by override (${posting.reasonCode}${describeOverridden(posting.overriddenRules)})` : posting.pickupMode === 'first_come' ? 'first come' : posting.pickupMode === 'bridge' ? 'through its Clinical Bridge callout' : 'from an offer'} {posting.filledAt && formatWhen(posting.filledAt)}
                    </>
                  ) : (
                    <>
                      <span className={`rounded-full border px-1.5 py-0.5 text-[9px] font-bold ${PHASE_CLASS[posting.phase]}`}>{PHASE_LABEL[posting.phase]}</span>
                      {live && <> · offered to {names.get(live.workerId) ?? live.workerId} until {formatWhen(live.expiresAt)}</>}
                      {posting.offers.filter((offer) => offer.answer === 'declined').length > 0 && <> · {posting.offers.filter((offer) => offer.answer === 'declined').length} declined</>}
                      {posting.blastAt && <> · everyone told {formatWhen(posting.blastAt)}</>}
                    </>
                  )}
                </div>
              </div>
              {onAssign && posting.status === 'open' && posting.phase === 'locked' && (
                <Button icon={UserPlus} variant="secondary" size="sm" onClick={() => onAssign(posting)}>
                  Assign
                </Button>
              )}
            </li>
          )
        })}
      </ul>
      {listed.length > POSTINGS_SHOWN && (
        <button type="button" onClick={() => setAll(!all)} className="self-start text-[11px] font-bold text-marine hover:text-marine-dk">
          {all ? 'Show fewer' : `Show all ${listed.length}`}
        </button>
      )}
    </div>
  )
}

function SwapsList({ board, names, units, onDecide }: { board: ScheduleBoardView; names: ReadonlyMap<string, string>; units: UnitNames; onDecide?: (swap: SwapRequest, decision: 'approve' | 'deny') => void }) {
  if (board.swaps.length === 0) {
    return <p className="text-[11px] text-ink-mid">No swaps asked for.</p>
  }
  return (
    <ul className="divide-y divide-marine/10 text-[11px]">
      {[...board.swaps].reverse().map((swap) => {
        const live = swap.offers.find((offer) => offer.answer === null)
        return (
          <li key={swap.swapId} className="flex flex-wrap items-center justify-between gap-2 py-1.5">
            <div>
              <span className="font-bold text-ink">{names.get(swap.requesterId) ?? swap.requesterId}</span>
              <span className="text-ink-mid"> gives {describeShiftById(swap.shiftId, board.schedule, units)}{swap.theirShiftId ? ` for ${describeShiftById(swap.theirShiftId, board.schedule, units)}` : ''}</span>
              <span className={`ml-2 font-bold ${SWAP_STATUS_CLASS[swap.status]}`}>{SWAP_STATUS_LABEL[swap.status]}</span>
              <div className="text-[10px] text-ink-mid">
                {swap.status === 'offered' && live && <>Offered to {names.get(live.workerId) ?? live.workerId} until {formatWhen(live.expiresAt)} · {swap.cascade.length} on the list</>}
                {swap.acceptedBy && <>Accepted by {names.get(swap.acceptedBy) ?? swap.acceptedBy}</>}
                {swap.approvalReasons.length > 0 && <> · {describeApprovalReasons(swap.approvalReasons)}</>}
                {swap.approval && <> · {swap.approval.decision} {formatWhen(swap.approval.at)}</>}
              </div>
            </div>
            {onDecide && swap.status === 'needs_approval' && (
              <span className="flex gap-1">
                <Button icon={Check} size="sm" onClick={() => onDecide(swap, 'approve')}>
                  Approve
                </Button>
                <Button icon={X} variant="secondary" size="sm" onClick={() => onDecide(swap, 'deny')}>
                  Deny
                </Button>
              </span>
            )}
          </li>
        )
      })}
    </ul>
  )
}

/** The board in full, read-only: the admin's facility dialog and the Partner Simulator. */
/** The month as it stands against the facility's minimum staffing (phase D), from the coverage read. */
function BoardCoverage({ coverage }: { coverage: ScheduleCoverageView | null | undefined }) {
  return coverage ? <CoverageGaps linesApplied={coverage.linesApplied} gaps={coverage.gaps} basisLabel="the month as it stands" /> : null
}

export function ScheduleBoardReadOnly({ board, coverage, units = new Map() }: { board: ScheduleBoardView; coverage?: ScheduleCoverageView | null; units?: UnitNames }) {
  const names = new Map(board.schedule.map((entry) => [entry.workerId, entry.name]))
  return (
    <div className="flex flex-col gap-3 text-[11px]">
      <p className="text-ink-mid">
        Change window {board.windowClosedAt ? `closed ${formatWhen(board.windowClosedAt)}` : `closes ${formatWhen(board.changeWindowClosesAt)}`} · {board.changeRequests.length} {board.changeRequests.length === 1 ? 'request' : 'requests'} · {board.swaps.length} {board.swaps.length === 1 ? 'swap' : 'swaps'} · {board.postings.filter((posting) => posting.status === 'open').length} open on the board
      </p>
      <BoardCoverage coverage={coverage} />
      <PostingsTable board={board} names={names} units={units} />
      <SwapsList board={board} names={names} units={units} />
      {board.ledger.length > 0 && (
        <ul className="text-[10px] text-ink-mid">
          {[...board.ledger].reverse().slice(0, 10).map((entry, index) => (
            <li key={`${entry.at}-${index}`}>
              {formatWhen(entry.at)} · {describeLedgerEntry(entry, names)}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

/**
 * The board card on the Dashboard's Month tab, After the release (scheduling rules plan phase C): what has
 * happened to the released month since its release, and what staff do
 * about it - close the change window early, vacate a shift, assign a
 * posting by override with a reason code, approve or deny a swap the rules
 * held. The board moves to the clock on every read.
 */
export function ScheduleBoardCard({ shown = true, facilityId, period }: { /** Whether the card is on screen: it opens the loading window only then. */ shown?: boolean; facilityId: string; period: string | null }) {
  const query = useScheduleBoard(facilityId, period)
  const coverage = useScheduleCoverage(facilityId, query.data?.period ?? period)
  const actions = useBoardActions(facilityId, period)
  const [assigning, setAssigning] = useState<{ posting: Posting; workerId: string; reasonCode: string } | null>(null)
  const [vacating, setVacating] = useState<{ workerId: string; shiftId: string; reason: string } | null>(null)
  const board = query.data ?? null
  const names = new Map((board?.schedule ?? []).map((entry) => [entry.workerId, entry.name]))
  const config = useFacilityConfig(facilityId)
  const units = unitNamesOf(config.data ? schedulingRulesOf(config.data).units : [])
  // First come, the default, nobody assigns a seat by hand (open shift lifecycle plan section 4); the override is the facility's order's, inside the lock.
  const facilityOrder = config.data ? schedulingRulesOf(config.data).coverage.openBoardMode === 'facility_order' : false
  const pending = [actions.closeWindow, actions.vacate, actions.assign, actions.decideSwap].some((mutation) => mutation.isPending)
  const failure = [actions.closeWindow, actions.vacate, actions.assign, actions.decideSwap].map((mutation) => mutation.error).find((error) => error) ?? null
  const month = board ? describePeriod(board.period) : period ? describePeriod(period) : 'the month'
  // Design screen 9: the board is read last because reading it brings it up to now, the slow part.
  useLoadingWindow({
    shown,
    visit: `board:${facilityId}:${period ?? 'focus'}`,
    title: 'Opening the board',
    subtitle: month,
    rank: 2,
    sources: [
      { key: 'board-rules', label: 'The facility’s rules', query: config },
      { key: 'board-coverage', label: 'Coverage against the minimum lines', query: coverage },
      {
        key: 'board',
        label: 'Bringing the board up to now',
        query,
        slowNote: 'Offers whose hours ran out pass to the next worker and the workers concerned are told. A busy month takes longer.',
        answered: (error) => (isNotReleased(error) || isNotFoundError(error) ? 'Not released yet: the board opens with the release.' : null),
      },
    ],
  })

  return (
    <div className="bg-white border border-marine/15 rounded p-4 flex flex-col gap-3">
      <div>
        <div className="text-sm font-black text-ink">
          After the release
          <span className="font-normal text-ink-mid"> · {month}</span>
          <EndpointHint className="ml-2">GET /facilities/{'{facilityId}'}/schedule/board</EndpointHint>
          <EndpointHint className="ml-2">GET /facilities/{'{facilityId}'}/schedule/coverage</EndpointHint>
        </div>
        <p className="text-[11px] text-ink-mid max-w-xl leading-relaxed">The change window, the trade board and the open board for the released month, with every change in the ledger. First come (the default) every eligible worker may take an open seat from the day it is posted, the race runs to the shift's start, and nobody assigns a worker by hand; in the facility's order offers pass on when their hours run out, a gap inside the urgent line goes to everyone first come, and in the last hours before a shift only your override assigns (Configuration › Automation).</p>
      </div>
      {query.isPending ? (
        <Skeleton lines={5} label="Loading the board" />
      ) : query.isError && (isNotReleased(query.error) || isNotFoundError(query.error)) ? (
        <p className="text-[11px] text-ink-mid">The board opens with the release. Build and release {month} above.</p>
      ) : query.isError || !board ? (
        <LoadFailure size="xs" message="The board could not be loaded." onRetry={() => void query.refetch()} retrying={query.isFetching} />
      ) : (
        <>
          <div className="flex flex-wrap items-center justify-between gap-2 text-[11px]">
            <span className="text-ink-mid">
              Change window {board.windowClosedAt ? `closed ${formatWhen(board.windowClosedAt)}` : `closes ${formatWhen(board.changeWindowClosesAt)}`} · {board.changeRequests.filter((request) => request.status === 'pending').length} pending {board.changeRequests.filter((request) => request.status === 'pending').length === 1 ? 'request' : 'requests'}
            </span>
            {!board.windowClosedAt && (
              <Button icon={DoorClosed} variant="secondary" size="sm" disabled={pending} onClick={() => actions.closeWindow.mutate()}>
                Close the window now
              </Button>
            )}
          </div>
          <BoardCoverage coverage={coverage.data} />
          {board.changeRequests.length > 0 && (
            <ul className="text-[10px] text-ink-mid">
              {board.changeRequests.map((request) => (
                <li key={request.requestId}>
                  {names.get(request.workerId) ?? request.workerId}: {request.kind === 'shift_type' ? `${describeShiftById(request.shiftId, board.schedule, units)} to ${request.toShiftType}` : `swap ${describeShiftById(request.shiftId, board.schedule, units)} with ${names.get(request.withWorkerId ?? '') ?? request.withWorkerId}${request.peerAnswer ? ` (${request.peerAnswer})` : ' (no answer yet)'}`} · {CHANGE_STATUS_LABEL[request.status]}
                  {request.reason && ` (${request.reason.replace(/_/g, ' ')})`}
                </li>
              ))}
            </ul>
          )}
          <div>
            <div className="mb-1 flex items-center justify-between">
              <div className="text-[10px] font-bold uppercase tracking-wide text-marine">The board</div>
              <Button icon={UserMinus} variant="secondary" size="sm" disabled={pending} onClick={() => setVacating({ workerId: board.schedule[0]?.workerId ?? '', shiftId: '', reason: '' })}>
                Vacate a shift
              </Button>
            </div>
            {vacating && (
              <form
                className="mb-2 flex flex-wrap items-end gap-2 rounded border border-marine/15 bg-canvas/60 p-2 text-[11px]"
                onSubmit={(event) => {
                  event.preventDefault()
                  actions.vacate.mutate(vacating, { onSuccess: () => setVacating(null) })
                }}
              >
                <label className="flex flex-col gap-0.5">
                  Worker
                  <select className={INPUT} value={vacating.workerId} onChange={(event) => setVacating({ ...vacating, workerId: event.target.value, shiftId: '' })}>
                    {board.schedule.map((entry) => (
                      <option key={entry.workerId} value={entry.workerId}>
                        {entry.name}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="flex flex-col gap-0.5">
                  Shift
                  <select className={INPUT} value={vacating.shiftId} onChange={(event) => setVacating({ ...vacating, shiftId: event.target.value })} required>
                    <option value="">Choose</option>
                    {(board.schedule.find((entry) => entry.workerId === vacating.workerId)?.assignments ?? []).map((assignment) => (
                      <option key={assignment.shiftId} value={assignment.shiftId}>
                        {describeShiftById(assignment.shiftId, board.schedule, units)}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="flex flex-col gap-0.5">
                  Reason
                  <input className={INPUT} value={vacating.reason} maxLength={200} onChange={(event) => setVacating({ ...vacating, reason: event.target.value })} required />
                </label>
                <Button icon={UserMinus} size="sm" type="submit" disabled={pending || !vacating.shiftId || !vacating.reason.trim()}>
                  Vacate
                </Button>
                <button type="button" onClick={() => setVacating(null)} className="text-[11px] font-bold text-marine">
                  Cancel
                </button>
              </form>
            )}
            <PostingsTable board={board} names={names} units={units} {...(facilityOrder ? { onAssign: (posting) => setAssigning({ posting, workerId: board.schedule[0]?.workerId ?? '', reasonCode: '' }) } : {})} />
            {assigning && (
              <form
                className="mt-2 flex flex-wrap items-end gap-2 rounded border border-coral/40 bg-coral-lt/60 p-2 text-[11px]"
                onSubmit={(event) => {
                  event.preventDefault()
                  actions.assign.mutate({ postingId: assigning.posting.postingId, workerId: assigning.workerId, reasonCode: assigning.reasonCode }, { onSuccess: () => setAssigning(null) })
                }}
              >
                <span className="font-bold text-ink">Assign {describePostingShift(assigning.posting.shift, units)} by override</span>
                <label className="flex flex-col gap-0.5">
                  To
                  <select className={INPUT} value={assigning.workerId} onChange={(event) => setAssigning({ ...assigning, workerId: event.target.value })}>
                    {board.schedule.map((entry) => (
                      <option key={entry.workerId} value={entry.workerId}>
                        {entry.name}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="flex flex-col gap-0.5">
                  Reason code
                  <input className={INPUT} value={assigning.reasonCode} maxLength={80} placeholder="CENSUS_SPIKE" onChange={(event) => setAssigning({ ...assigning, reasonCode: event.target.value })} required />
                </label>
                <span className="basis-full text-[10px] text-ink-mid">An override can pass the rest rules (a day shift after an evening, too many days in a row) and records which. It never assigns another role, a unit the worker does not work, a missing or lapsed credential, or a second shift the same day.</span>
                <Button icon={UserPlus} size="sm" type="submit" disabled={pending || !assigning.reasonCode.trim()}>
                  Assign
                </Button>
                <button type="button" onClick={() => setAssigning(null)} className="text-[11px] font-bold text-marine">
                  Cancel
                </button>
              </form>
            )}
          </div>
          <div>
            <div className="mb-1 text-[10px] font-bold uppercase tracking-wide text-marine">Swaps</div>
            <SwapsList board={board} names={names} units={units} onDecide={(swap, decision) => actions.decideSwap.mutate({ swapId: swap.swapId, decision })} />
          </div>
          {board.ledger.length > 0 && (
            <div>
              <div className="mb-1 text-[10px] font-bold uppercase tracking-wide text-marine">What changed</div>
              <ul className="text-[10px] text-ink-mid">
                {[...board.ledger].reverse().slice(0, 12).map((entry, index) => (
                  <li key={`${entry.at}-${index}`}>
                    {formatWhen(entry.at)} · {describeLedgerEntry(entry, names)}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {failure && (
            <p role="alert" className="text-[11px] text-coral">
              {failure instanceof ApiError ? failure.message : 'That could not be done. Try again.'}
            </p>
          )}
        </>
      )}
    </div>
  )
}
